import { useUIv2 } from '@/hooks/useUIv2'
import NewDigitalCoworkerV2 from './NewDigitalCoworkerV2'
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ArrowRight, ChevronRight, CircleAlert, Loader2, Send } from 'lucide-react'
import PageLayout from '@/components/layout/PageLayout'
import { apiService } from '@/service/api'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'

const DEPARTMENTS = [
  'Chemical Engineering',
  'Mechanical Engineering',
  'Electrical Engineering',
  'Default',
] as const

const NewDigitalCoworker = () => {
  const isV2 = useUIv2()
  if (isV2) return <NewDigitalCoworkerV2 />
  return <NewDigitalCoworkerLegacy />
}

const NewDigitalCoworkerLegacy = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [type, setType] = useState<'me' | 'colleague'>('me')
  const [name, setName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [department, setDepartment] = useState('Chemical Engineering')
  const [expertise, setExpertise] = useState('')
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [colleagueEmail, setColleagueEmail] = useState('')
  const [isSendingInvite, setIsSendingInvite] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Restore form data and existing coworker id if coming back from connect-accounts
  const existingCoworkerId = location.state?.coworkerId as string | undefined
  useEffect(() => {
    const savedData = location.state?.formData
    if (savedData) {
      setType(savedData.type || 'me')
      setName(savedData.name || '')
      setJobTitle(savedData.jobTitle || '')
      const dept = savedData.department
      setDepartment(DEPARTMENTS.includes(dept) ? dept : 'Chemical Engineering')
      setExpertise(savedData.expertise || '')
    }
  }, [location.state])

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    const reader = new FileReader()
    reader.onloadend = () => setPhotoPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async () => {
    if (!name.trim() || !jobTitle.trim()) return

    setIsSubmitting(true)
    try {
      const formData = { type, name, jobTitle, department, expertise }
      const payload = {
        name: name.trim(),
        job_title: jobTitle.trim(),
        department,
        expertise: expertise.trim() || undefined,
        type,
      }

      if (existingCoworkerId) {
        await apiService.updateDigitalCoworker(
          existingCoworkerId,
          payload,
          photoFile ?? undefined,
        )
        toast.success(t('digital_coworker:edit_success'))
        navigate('/digital-coworker')
      } else {
        const result = await apiService.createDigitalCoworker(
          payload,
          photoFile ?? undefined,
        )
        navigate('/digital-coworker/connect-accounts', {
          state: { formData, coworkerId: result.digital_coworker.id },
        })
      }
    } catch (error) {
      console.error('Failed to save digital coworker:', error)
      toast.error(t(existingCoworkerId ? 'digital_coworker:toast.edit_error' : 'digital_coworker:toast.create_error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/digital-coworker')
  }

  const handleSendInvite = async () => {
    if (!colleagueEmail.trim()) return
    setIsSendingInvite(true)
    try {
      const { success } = await apiService.inviteColleague({
        colleague_email: colleagueEmail.trim(),
        webapp_origin: window.location.origin,
      })
      if (!success) throw new Error('invite_failed')
      toast.success(t('digital_coworker:new_coworker.colleague_invite.invite_success'))
      navigate('/digital-coworker')
    } catch (error) {
      console.error('Failed to send colleague invite:', error)
      toast.error(t('digital_coworker:new_coworker.colleague_invite.invite_error'))
    } finally {
      setIsSendingInvite(false)
    }
  }

  const isFormValid = name.trim() !== '' && jobTitle.trim() !== ''

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto py-8">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          <button
            onClick={() => navigate('/digital-coworker')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-dark-text transition-colors"
          >
            {t('digital_coworker:breadcrumb.list')}
          </button>
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>

        <div className="space-y-8">
          {/* Header Section */}
          <div>
            <h2 className="text-lg font-semibold mb-2">{t('digital_coworker:new_coworker.who_for_title')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('digital_coworker:new_coworker.who_for_subtitle')}
            </p>
          </div>

          {/* Type Selection */}
          <RadioGroup value={type} onValueChange={(value) => setType(value as 'me' | 'colleague')}>
            <div className="grid grid-cols-2 gap-4">
              <label
                htmlFor="type-me"
                className={`flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  type === 'me'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <RadioGroupItem value="me" id="type-me" />
                  <span className="font-semibold text-sm">{t('digital_coworker:new_coworker.for_me')}</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
                  {t('digital_coworker:new_coworker.for_me_desc')}
                </p>
              </label>

              <label
                htmlFor="type-colleague"
                className={`flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  type === 'colleague'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <RadioGroupItem value="colleague" id="type-colleague" />
                  <span className="font-semibold text-sm">{t('digital_coworker:new_coworker.for_colleague')}</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
                  {t('digital_coworker:new_coworker.for_colleague_desc')}
                </p>
              </label>
            </div>
          </RadioGroup>

          {type === 'colleague' ? (
            /* Colleague Invite Section */
            <div className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="colleague-email">
                  {t('digital_coworker:new_coworker.colleague_invite.email_label')}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="colleague-email"
                  type="email"
                  placeholder={t('digital_coworker:new_coworker.colleague_invite.email_placeholder')}
                  value={colleagueEmail}
                  onChange={(e) => setColleagueEmail(e.target.value)}
                />
              </div>

              {/* How does the invite work? Banner */}
              <div className="flex gap-2 items-start bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg px-2.5 py-2">
                <CircleAlert className="h-5 w-5 text-gray-700 dark:text-gray-300 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium text-gray-950 dark:text-dark-text">
                    {t('digital_coworker:new_coworker.colleague_invite.how_invite_works_title')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                    {t('digital_coworker:new_coworker.colleague_invite.how_invite_works_desc')}
                  </p>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-6 border-t">
                <Button variant="outline" onClick={handleCancel} disabled={isSendingInvite}>
                  {t('common:buttons.cancel')}
                </Button>
                <Button
                  onClick={handleSendInvite}
                  disabled={!colleagueEmail.trim() || isSendingInvite}
                  className="gap-2"
                >
                  {isSendingInvite ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('digital_coworker:new_coworker.colleague_invite.sending_invite')}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      {t('digital_coworker:new_coworker.colleague_invite.send_invite')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            /* For Me — Your Details Section */
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-semibold mb-1">{t('digital_coworker:new_coworker.your_details_title')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('digital_coworker:new_coworker.your_details_subtitle')}</p>
              </div>

              {/* Avatar Upload */}
              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center overflow-hidden cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl text-orange-600 font-semibold">
                      {name ? name.charAt(0).toUpperCase() : '?'}
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  {t('digital_coworker:new_coworker.change_photo')}
                </Button>
              </div>

              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('digital_coworker:new_coworker.name_label')}<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder={t('digital_coworker:new_coworker.name_placeholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              {/* Job Title Input */}
              <div className="space-y-2">
                <Label htmlFor="jobTitle">
                  {t('digital_coworker:new_coworker.job_title_label')}<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="jobTitle"
                  placeholder={t('digital_coworker:new_coworker.job_title_placeholder')}
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>

              {/* Department Select */}
              <div className="space-y-2">
                <Label htmlFor="department">
                  {t('digital_coworker:new_coworker.department_label')}<span className="text-red-500">*</span>
                </Label>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger id="department">
                    <SelectValue placeholder={t('digital_coworker:new_coworker.department_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {t(`digital_coworker:new_coworker.departments.${dept}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Areas of Expertise Input */}
              <div className="space-y-2">
                <Label htmlFor="expertise">{t('digital_coworker:new_coworker.expertise_label')}</Label>
                <Input
                  id="expertise"
                  placeholder={t('digital_coworker:new_coworker.expertise_placeholder')}
                  value={expertise}
                  onChange={(e) => setExpertise(e.target.value)}
                />
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-6 border-t">
                <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                  {t('common:buttons.cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className="gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t(existingCoworkerId ? 'digital_coworker:new_coworker.saving' : 'digital_coworker:new_coworker.creating')}
                    </>
                  ) : (
                    <>
                      {t('digital_coworker:buttons.continue')}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  )
}

export default NewDigitalCoworker
